/**
 * Chat session sidebar component.
 * Lists past chat sessions with rename, delete, and context selection.
 */

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useChatSessions,
  useRenameChatSession,
  useDeleteChatSession,
} from "../hooks/useChatSessions";
import { Plus, MoreHorizontal, Trash2, Pencil, MessageSquare } from "lucide-react";

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface ChatSidebarProps {
  projectId?: string;
  scope: string;
  activeSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewSession: () => void;
  selectedContextIds: string[];
  onToggleContext: (sessionId: string) => void;
}

export function ChatSidebar({
  projectId,
  scope,
  activeSessionId,
  onSelectSession,
  onNewSession,
  selectedContextIds,
  onToggleContext,
}: ChatSidebarProps) {
  const { t } = useTranslation();
  const { data: sessionsData, isLoading, isError } = useChatSessions({ projectId, scope });
  const renameMutation = useRenameChatSession();
  const deleteMutation = useDeleteChatSession();

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    }
    if (menuOpenId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpenId]);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (renamingId) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingId]);

  const sessions = sessionsData?.sessions || [];

  const handleRenameSubmit = (sessionId: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    renameMutation.mutate(
      { sessionId, title: trimmed },
      { onSuccess: () => setRenamingId(null) }
    );
  };

  const handleDelete = (sessionId: string) => {
    deleteMutation.mutate(
      { sessionId },
      {
        onSuccess: () => {
          setConfirmDeleteId(null);
          if (activeSessionId === sessionId) {
            onNewSession();
          }
        },
      }
    );
  };

  return (
    <div className="w-56 shrink-0 flex flex-col bg-card rounded-lg border border-border overflow-hidden">
      {/* New Chat Button */}
      <div className="p-2 border-b border-border">
        <button
          onClick={onNewSession}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t("chatSidebar.newChat")}
        </button>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-3 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <p className="p-4 text-xs text-destructive">{t("chatSidebar.loadError")}</p>
        ) : sessions.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-xs">
            <MessageSquare className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
            <p>{t("chatSidebar.noHistory")}</p>
          </div>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {sessions.map((session) => {
              const isActive = session.id === activeSessionId;
              const isRenaming = session.id === renamingId;

              return (
                <div key={session.id} className="relative group">
                  {/* Confirm Delete Dialog */}
                  {confirmDeleteId === session.id && (
                    <div className="absolute inset-0 z-10 bg-card border border-red-300 dark:border-red-800 rounded-lg p-2 flex flex-col gap-1.5">
                      <p className="text-xs text-foreground leading-tight">
                        {t("chatSidebar.deleteConfirm")}
                      </p>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="flex-1 px-2 py-1 text-xs rounded bg-muted hover:bg-accent transition-colors"
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          onClick={() => handleDelete(session.id)}
                          disabled={deleteMutation.isPending}
                          className="flex-1 px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {t("chatSidebar.delete")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex items-start gap-1.5 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                      isActive
                        ? "bg-blue-50 dark:bg-blue-950 border-l-2 border-blue-500"
                        : "hover:bg-accent"
                    }`}
                    onClick={() => {
                      if (!isRenaming) onSelectSession(session.id);
                    }}
                  >
                    {/* Context checkbox (only for non-active sessions) */}
                    {!isActive && (
                      <input
                        type="checkbox"
                        checked={selectedContextIds.includes(session.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleContext(session.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 shrink-0 h-3.5 w-3.5 rounded border-border text-blue-600 focus:ring-blue-500"
                        title={t("chatSidebar.includeAsContext")}
                      />
                    )}

                    <div className="flex-1 min-w-0">
                      {isRenaming ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(session.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRenameSubmit(session.id);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-1.5 py-0.5 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <>
                          <div className="text-xs font-medium text-foreground truncate">
                            {session.title || `Chat ${session.message_count}`}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {formatRelativeDate(session.updated_at || session.created_at)}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Action menu trigger */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === session.id ? null : session.id);
                      }}
                      className="shrink-0 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Dropdown menu */}
                  {menuOpenId === session.id && (
                    <div
                      ref={menuRef}
                      className="absolute right-1 top-8 z-20 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[120px]"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenamingId(session.id);
                          setRenameValue(session.title || "");
                          setMenuOpenId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-accent transition-colors"
                      >
                        <Pencil className="h-3 w-3" />
                        {t("chatSidebar.rename")}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(session.id);
                          setMenuOpenId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-accent transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                        {t("chatSidebar.delete")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renameMutation.isError && <p className="p-2 border-t border-border text-xs text-destructive">{t("chatSidebar.renameError")}</p>}
      {deleteMutation.isError && <p className="p-2 border-t border-border text-xs text-destructive">{t("chatSidebar.deleteError")}</p>}
    </div>
  );
}
